import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from './ui/command';
import { DialogTitle } from './ui/dialog';
import { Ticket, CheckSquare, FileText, Megaphone, Newspaper, Users, Image as ImageIcon, User } from 'lucide-react';
import { globalSearch } from '../services/api';

const KIND_UI = {
  ticket: { icon: Ticket, group: 'Tickets' },
  task: { icon: CheckSquare, group: 'Tasks' },
  doc: { icon: FileText, group: 'Documents' },
  broadcast: { icon: Megaphone, group: 'Broadcasts' },
  news: { icon: Newspaper, group: 'Company News' },
  person: { icon: User, group: 'People' },
  gallery: { icon: ImageIcon, group: 'Galleries' },
  user: { icon: Users, group: 'Users' },
};

const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  // Global keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Debounced search on query change
  useEffect(() => {
    if (!open) return;
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await globalSearch(q.trim());
        setResults(res.results || []);
      } catch (_) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [q, open]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setQ('');
      setResults([]);
    }
  }, [open]);

  const grouped = useMemo(() => {
    const g = {};
    for (const r of results) {
      const meta = KIND_UI[r.kind] || KIND_UI.doc;
      if (!g[meta.group]) g[meta.group] = { icon: meta.icon, items: [] };
      g[meta.group].items.push(r);
    }
    return g;
  }, [results]);

  const jump = (item) => {
    setOpen(false);
    if (item.url) navigate(item.url);
  };

  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <DialogTitle className="sr-only">Global search</DialogTitle>
        <CommandInput
          placeholder="Search tickets, tasks, docs, people, news…"
          value={q}
          onValueChange={setQ}
          data-testid="command-palette-input"
        />
        <CommandList>
          {q.trim().length < 2 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              Start typing to search across the whole workspace.
              <div className="mt-3 text-[11px] tracking-widest uppercase opacity-60">
                ⌘K / Ctrl-K anywhere to open
              </div>
            </div>
          )}
          {q.trim().length >= 2 && !loading && results.length === 0 && (
            <CommandEmpty>No results for "{q}".</CommandEmpty>
          )}
          {loading && (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">Searching…</div>
          )}
          {Object.entries(grouped).map(([group, { icon: Icon, items }]) => (
            <CommandGroup key={group} heading={group}>
              {items.map((item) => (
                <CommandItem
                  key={`${item.kind}-${item.id}`}
                  value={`${item.title} ${item.subtitle}`}
                  onSelect={() => jump(item)}
                  data-testid={`command-item-${item.kind}-${item.id}`}
                >
                  <Icon className="text-[#0f5c3d]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{item.title}</div>
                    {item.subtitle && (
                      <div className="text-[11px] text-muted-foreground truncate">
                        {item.subtitle}
                      </div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
};

export default CommandPalette;
